import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { usePatchUpdateResource } from '@app/src/api/updateHooks'
import TextField from '@app/src/components/Ui/TextField'
import { useProvidersModal } from '@app/src/context/ProvidersModalContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import type { Provider } from '@app/src/types/organizations'
import { validURL } from '@app/src/utils/helpers'
import { NotificationSeverity } from '@app/src/wf-constants'
import { Stack } from '@mui/material'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import { useDrawer } from './DrawerContext'
import DrawerView from './DrawerView'

interface DrawerViewEditWebsiteProps {
  provider: Provider
  onSaved?: (provider: Provider) => void
}

const DrawerViewEditWebsite: React.FC<DrawerViewEditWebsiteProps> = ({ provider, onSaved }) => {
  const { formatMessage } = useIntl()
  const { closeDrawer } = useDrawer()
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()
  const { register, errors, handleSubmit, setError } = useForm<Partial<Provider>>({
    mode: 'onChange',
    defaultValues: {
      websiteAddress: provider.websiteAddress || '',
    },
  })
  const queryClient = useQueryClient()
  const { mutateAsync, isPending: isLoading } = usePatchUpdateResource<Partial<Provider>>()
  const { updateProviderToConnect } = useProvidersModal()

  const onSubmit = async (data: Partial<Provider>): Promise<void> => {
    const websiteAddress = data.websiteAddress?.trim() || ''
    await mutateAsync(
      {
        url: endpoints.provider(provider.id),
        body: { websiteAddress },
      },
      {
        onSuccess: updatedProvider => {
          const savedProvider = { ...provider, websiteAddress: updatedProvider.websiteAddress ?? '' }
          updateProviderToConnect(savedProvider)
          onSaved?.(savedProvider)

          showSnackbar({
            message: formatMessage({ id: 'form.scheduleProviderScreening.websiteMissing.success' }),
            severity: NotificationSeverity.success,
            disableAutoClose: true,
          })
          queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderCollection] })
          queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderCount] })
          closeDrawer()
        },
        onError: error => {
          if (error.isValidationError) {
            error.setFormValidationErrors(setError)
            return
          }
          showErrorNotification({ requestError: error, disableAutoClose: true })
        },
      },
    )
  }

  return (
    <DrawerView
      title={formatMessage({ id: 'general.edit' })}
      subTitle={provider.name}
      showBackButton
      stackButtons
      onFormSubmit={handleSubmit(onSubmit)}
      buttons={[
        {
          label: formatMessage({ id: 'referral.sendInvite.save' }),
          variant: 'contained',
          type: 'submit',
          loading: isLoading,
        },
      ]}
    >
      <Stack spacing={2} p={2}>
        <TextField
          hoveringLabel
          value={provider.name || ''}
          fullWidth
          required
          name="name"
          label={formatMessage({ id: 'schemas.company.name' })}
          error={Boolean(errors?.name)}
          type="text"
          helperText={errors?.name?.message}
          disabled
        />

        <TextField
          hoveringLabel
          value={provider.country?.name || ''}
          fullWidth
          name="name"
          label={formatMessage({ id: 'resourceCollections.general.country' })}
          error={Boolean(errors?.name)}
          type="text"
          helperText={errors?.countryId?.message}
          disabled
        />

        <TextField
          hoveringLabel
          placeholder={formatMessage({ id: 'textFieldPlaceholders.websiteExample' })}
          inputRef={register({
            required: formatMessage({ id: 'form.validation.required' }),
            validate: value => validURL(value) || formatMessage({ id: 'notifications.errorInvalidWebsite' }),
          })}
          fullWidth
          required
          name="websiteAddress"
          label={formatMessage({ id: 'schemas.organization.websiteAddress' })}
          error={Boolean(errors?.websiteAddress)}
          type="text"
          helperText={errors?.websiteAddress?.message}
        />
      </Stack>
    </DrawerView>
  )
}

export default DrawerViewEditWebsite
